/**
 * Zone targeting utilities for strategem targeting
 * @module targeting/zoneTargeting
 */

import { getTargetingBorderStyles } from './TargetingBorder';

/**
 * Get zone highlighting styles for strategem targeting
 * @param {boolean} strategemTargetMode - Whether strategem targeting is active
 * @param {Object} strategemTargetInfo - Strategem targeting information
 * @param {string} zoneType - Type of zone targeting ('zone' or 'unit')
 * @returns {Object} - CSS styles for zone highlighting
 */
export function getZoneTargetingStyles(strategemTargetMode, strategemTargetInfo, zoneType = 'zone', zoneKey) {
  // Check both strategemTargetInfo.type AND targetingOptions for zone targeting
  const targetingOptions = strategemTargetInfo?.targetingOptions?.[0];
  const isZoneTargeting = strategemTargetInfo?.type === zoneType || 
                          (zoneType === 'zone' && targetingOptions?.targeting === 'zone');
  
  if (
    strategemTargetMode &&
    isZoneTargeting &&
    typeof zoneKey === 'string'
  ) {
    // Check targeting rules to determine which zones should be highlighted (targetingOptions is an array)
    const targetingRules = strategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent'; // Default to opponent
    
    const isOpponentZone = zoneKey.startsWith('opponent_');
    const isFriendlyZone = !isOpponentZone && (zoneKey === 'front' || zoneKey === 'flank');
    
    // Determine if this zone should be highlighted based on player restriction
    let shouldHighlight = false;
    if (playerRestriction === 'both') {
      shouldHighlight = isOpponentZone || isFriendlyZone;
    } else if (playerRestriction === 'opponent') {
      shouldHighlight = isOpponentZone;
    } else if (playerRestriction === 'friendly') {
      shouldHighlight = isFriendlyZone;
    }
    
    if (shouldHighlight) {
      // Use unified targeting border system (outline so zone separator borders stay visible)
      const styles = getTargetingBorderStyles(true);
      return styles || {
        outline: "3px solid #ffff00",
        outlineOffset: "0",
        cursor: "pointer",
        position: "relative"
      };
    }
  }
  return {
    outline: "none",
    cursor: "default"
  };
}

/**
 * Handle zone click for strategem targeting
 * @param {string} zone - Zone name (e.g., 'front', 'flank', 'opponent_front', 'opponent_flank')
 * @param {boolean} strategemTargetMode - Whether strategem targeting is active
 * @param {number} selectedStrategemId - Selected strategem ID
 * @param {Object} strategemTargetInfo - Strategem targeting information
 * @param {Function} targetStrategem - Strategem targeting function
 * @param {Object} ws - WebSocket connection
 * @param {Object} gameId - Game ID
 * @param {Function} setError - Error setter
 * @param {Function} setStrategemTargetMode - Strategem target mode setter
 * @param {Function} setSelectedStrategemId - Selected strategem ID setter
 * @param {Function} setStrategemTargetInfo - Strategem target info setter
 * @param {Object} targetingData - Database targeting rules (optional for validation)
 * @param {Object} animationConfig - Animation configuration (optional)
 */
export function handleZoneClick(
  zone,
  strategemTargetMode,
  selectedStrategemId,
  strategemTargetInfo,
  targetStrategem,
  ws,
  gameId,
  setError,
  setStrategemTargetMode,
  setSelectedStrategemId,
  setStrategemTargetInfo,
  targetingData,
  animationConfig = null
) {
  console.log('🎯🎯🎯 handleZoneClick CALLED!', {
    zone,
    strategemTargetMode,
    selectedStrategemId,
    strategemTargetInfoType: strategemTargetInfo?.type,
    hasAnimationConfig: !!animationConfig
  });
  
  // Check if strategem targeting mode is active and targeting zones
  // Check both strategemTargetInfo.type AND targetingOptions for zone targeting
  const targetingOptions = strategemTargetInfo?.targetingOptions?.[0];
  const isZoneTargeting = strategemTargetInfo?.type === 'zone' || targetingOptions?.targeting === 'zone';
  
  console.log('🎯 handleZoneClick: Zone targeting check', {
    strategemInfoType: strategemTargetInfo?.type,
    targetingOptionsType: targetingOptions?.targeting,
    isZoneTargeting,
    zone
  });
  
  if (
    strategemTargetMode &&
    selectedStrategemId &&
    isZoneTargeting &&
    typeof zone === 'string'
  ) {
    // Determine if this is a friendly or opponent zone
    const isOpponentZone = zone.startsWith('opponent_');
    const isFriendlyZone = !isOpponentZone && (zone === 'front' || zone === 'flank');
    
    // Check targeting rules from strategemTargetInfo or targetingData (it's an array)
    const targetingRules = strategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent'; // Default to opponent only
    
    // Validate zone based on player restriction
    let isValidTarget = false;
    if (playerRestriction === 'both') {
      isValidTarget = isOpponentZone || isFriendlyZone;
    } else if (playerRestriction === 'opponent') {
      isValidTarget = isOpponentZone;
    } else if (playerRestriction === 'friendly') {
      isValidTarget = isFriendlyZone;
    }
    
    if (isValidTarget) {
      console.log(`🎯 ZONE CLICK: Targeting ${zone} with strategem ${selectedStrategemId}`);
      console.log(`🎬 ZONE CLICK: Animation config:`, animationConfig);
      targetStrategem("zone", zone, null, null, null, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
      return true; // Handled
    } else {
      console.log(`🎯 ZONE CLICK: Zone ${zone} not valid for player restriction "${playerRestriction}"`);
    }
  }
  return false; // Not handled
}

/**
 * Check if a zone should be highlighted for targeting
 * @param {boolean} strategemTargetMode - Whether strategem targeting is active
 * @param {Object} strategemTargetInfo - Strategem targeting information
 * @param {string} zoneType - Type of zone targeting ('zone' or 'unit')
 * @returns {boolean} - Whether zone should be highlighted
 */
export function shouldHighlightZone(strategemTargetMode, strategemTargetInfo, zoneType = 'zone') {
  return strategemTargetMode && strategemTargetInfo?.type === zoneType;
}

/**
 * Get zone targeting message for UI
 * @param {boolean} strategemTargetMode - Whether strategem targeting is active
 * @param {Object} strategemTargetInfo - Strategem targeting information
 * @returns {string|null} - Targeting message or null
 */
export function getZoneTargetingMessage(strategemTargetMode, strategemTargetInfo) {
  if (strategemTargetMode && strategemTargetInfo?.type === 'zone') {
    return "Select a zone to target with your strategem";
  }
  return null;
}
